import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { matchAreaGuideRegion } from "src/data/areaGuides";

export type Poi = { name: string; address?: string; distance?: number };

export type PoiCategoryKey =
  | "coffee"
  | "food"
  | "shop"
  | "police"
  | "transport"
  | "school";

export const POI_CATEGORIES: {
  key: PoiCategoryKey;
  label: string;
  mapboxCategory: string;
}[] = [
  { key: "coffee", label: "Coffee", mapboxCategory: "coffee" },
  { key: "food", label: "Food", mapboxCategory: "food" },
  { key: "shop", label: "Shops", mapboxCategory: "shop" },
  { key: "police", label: "Police", mapboxCategory: "police" },
  { key: "transport", label: "Transport", mapboxCategory: "transit_station" },
  { key: "school", label: "Schools", mapboxCategory: "school" },
];

export const EMPTY_POIS_BY_CATEGORY: Record<PoiCategoryKey, Poi[]> = {
  coffee: [],
  food: [],
  shop: [],
  police: [],
  transport: [],
  school: [],
};

const OVERVIEW_KEYS: PoiCategoryKey[] = ["coffee", "food", "shop", "police"];

type TabKey = "overview" | "transport" | "area-guide";

const TABS: { key: TabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "transport", label: "Transport" },
  { key: "area-guide", label: "Area Guide" },
];


function PoiList({
  categories,
  poisByCategory,
}: {
  categories: { key: PoiCategoryKey; label: string }[];
  poisByCategory: Record<PoiCategoryKey, Poi[]>;
}) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {categories.map(({ key, label }) => {
        const items = poisByCategory[key] || [];
        return (
          <div
            key={key}
            className="border border-solid border-[rgba(0,0,0,0.08)] bg-white/80 p-4"
          >
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="font-teko text-xl">{label}</div>
              <div className="text-xs text-text-grey">{items.length} found</div>
            </div>
            {items.length === 0 ? (
              <div className="text-sm text-text-grey">No results nearby.</div>
            ) : (
              <ul className="space-y-3">
                {items.map((poi, idx) => (
                  <li
                    key={`${key}-${poi.name}-${idx}`}
                    className="border-t border-t-solid border-t-[rgba(0,0,0,0.06)] pt-3 first:border-t-0 first:pt-0"
                  >
                    <div className="font-semibold text-text-obsidian">
                      {poi.name}
                    </div>
                    {poi.address ? (
                      <div className="text-sm text-text-grey">{poi.address}</div>
                    ) : null}
                    {typeof poi.distance === "number" ? (
                      <div className="text-xs text-text-grey">
                        ~{poi.distance.toFixed(0)} m away
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface NeighbourhoodTabProps {
  postcode: string;
  displayAddress: string;
  staticMapUrl: string;
  staticMapLoading: boolean;
  staticMapError: string;
  onMapClick: () => void;
  onMapLoad?: () => void;
  onMapError?: () => void;
  poisByCategory: Record<PoiCategoryKey, Poi[]>;
  loadingPois: boolean;
  poisError: string;
}

const NeighbourhoodTab: React.FC<NeighbourhoodTabProps> = ({
  postcode,
  displayAddress,
  staticMapUrl,
  staticMapLoading,
  staticMapError,
  onMapClick,
  onMapLoad,
  onMapError,
  poisByCategory,
  loadingPois,
  poisError,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>("overview");

  const matchedRegion = useMemo(
    () => matchAreaGuideRegion(postcode, displayAddress),
    [postcode, displayAddress]
  );

  const overviewCategories = POI_CATEGORIES.filter((c) =>
    OVERVIEW_KEYS.includes(c.key)
  );
  const transportCategories = POI_CATEGORIES.filter(
    (c) => c.key === "transport" || c.key === "school"
  );

  return (
    <div className="bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] shadow-soft overflow-hidden">
      <div className="border-b border-b-solid border-b-[rgba(0,0,0,0.06)] p-4 flex items-center justify-between gap-3">
        <div>
          <div className="font-manrope text-xs uppercase tracking-wider text-text-slate">
            Neighbourhood
          </div>
          <div className="text-xs text-text-grey">
            {postcode || displayAddress}
          </div>
        </div>
        {staticMapUrl ? (
          <a
            href={staticMapUrl}
            target="_blank"
            rel="noreferrer"
            className="text-sm underline text-text-grey hover:text-text-obsidian"
          >
            Open map image
          </a>
        ) : null}
      </div>

      <div className="relative h-[320px] w-full bg-[#eee]">
        {staticMapUrl ? (
          <img
            src={staticMapUrl}
            alt="Property location map"
            className="absolute inset-0 w-full h-full object-cover cursor-pointer"
            title="Open in map view"
            onClick={onMapClick}
            onLoad={onMapLoad}
            onError={onMapError}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-text-grey text-sm">
            Map loading…
          </div>
        )}
        {staticMapLoading ? (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-text-grey text-sm">
            Loading map…
          </div>
        ) : null}
      </div>

      {staticMapError ? (
        <div className="px-6 pt-4 text-red-600 text-sm">{staticMapError}</div>
      ) : null}

      <div className="flex border-b border-b-solid border-b-[rgba(0,0,0,0.06)]">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            className={`flex-1 px-4 py-3 font-teko text-lg uppercase tracking-wide transition-colors ${
              activeTab === key
                ? "bg-brand-yellow/20 text-text-obsidian border-b-2 border-b-brand-yellow"
                : "text-text-slate hover:bg-bg-porcelain"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {poisError ? (
          <div className="text-red-600 text-sm mb-3">{poisError}</div>
        ) : null}

        {activeTab === "overview" && (
          <>
            <p className="font-manrope text-sm text-text-slate mb-4">
              Local amenities within walking distance — coffee, dining, shops,
              and essential services.
            </p>
            {loadingPois ? (
              <div className="text-text-grey text-sm">Loading POIs...</div>
            ) : (
              <PoiList
                categories={overviewCategories}
                poisByCategory={poisByCategory}
              />
            )}
          </>
        )}

        {activeTab === "transport" && (
          <>
            <p className="font-manrope text-sm text-text-slate mb-4">
              Nearby stations, stops, and schools for your daily commute.
            </p>
            {loadingPois ? (
              <div className="text-text-grey text-sm">Loading transport...</div>
            ) : (
              <PoiList
                categories={transportCategories}
                poisByCategory={poisByCategory}
              />
            )}
          </>
        )}

        {activeTab === "area-guide" && (
          <div className="space-y-4">
            <div>
              <span className="font-manrope text-xs uppercase tracking-wider text-text-slate">
                Region
              </span>
              <h4 className="font-teko text-3xl text-text-obsidian mt-1">
                {matchedRegion?.title ?? "London"}
              </h4>
            </div>
            <p className="font-manrope text-base leading-relaxed text-text-slate">
              {matchedRegion
                ? `${matchedRegion.tagline}. ${matchedRegion.overview[0]}`
                : "A premier agency for luxury rentals across the capital. Seamless service, prime locations, and neighbourhoods to suit every lifestyle."}
            </p>
            {matchedRegion ? (
              <Link
                to={`/guides/areas/${matchedRegion.slug}`}
                className="inline-block font-teko text-sm uppercase border-b border-b-solid border-b-brand-yellow text-text-obsidian hover:text-brand-gold transition-colors"
              >
                Read full area guide
              </Link>
            ) : null}
            <Link
              to="/guides/areas"
              className="block font-manrope text-sm text-text-grey hover:text-text-obsidian underline"
            >
              Browse all London area guides
            </Link>
          </div>
        )}
      </div>

      <p className="px-6 pb-4 text-xs text-text-grey leading-relaxed border-t border-t-solid border-t-[rgba(0,0,0,0.06)] pt-4">
        Nearby places are sourced from{" "}
        <span className="text-text-slate">Mapbox Search Box API</span> and are
        estimates only — actual distances, opening hours, and availability may
        differ. Actual details prevail.
      </p>
    </div>
  );
};

export default NeighbourhoodTab;
